import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Machine } from "../GameComponents/Machine";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { GameData } from "../Channels/Common/Datas/GameData";
import { DataManager } from "../Managers/DataManager";
import { ModuleManager } from "../Managers/ModuleManager";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { ReelExtended } from "./ReelExtended";
import { Settings } from "./Settings";
import { AppContext } from "../../Common/AppContext";

@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            PlayList.stop("SND_BaseFSTeaser");

            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (DEBUG) {
            this.log("spinStop: " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (
                !ModuleManager.freespinsActive() &&
                !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
            ) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }
            if (force && this._teasing != null) {
                for (let key in this._teasing.childs) {
                    if (this._teasing.childs[key] instanceof SwitchElement) {
                        this._teasing.childs[key].goToCase("EMPTY");
                    }
                }
            }

            this._canTease2 = force ? false : this.checkTeasing(2);
            this._canTease3 = force ? false : this.checkTeasing(3);
            this._canTease4 = force ? false : this.checkTeasing(4);

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);

                            let stopDelay = this._stopDelay * i;
                            let useForceCustomDelay = false;

                            if (force || (!this._canTease2 && !this._canTease3 && !this._canTease4)) {
                                this._reels[i].spinStop(force, stopDelay, useForceCustomDelay);
                            } else {
                                if (this._canTease2 && this._canTease3 && this._canTease4) {
                                    if (i === 0 || i === 1) {
                                        this._reels[i].spinStop(force, stopDelay, useForceCustomDelay);
                                    }
                                } else if (this._canTease3 && this._canTease4) {
                                    if (i === 0 || i === 1 || i === 2) {
                                        this._reels[i].spinStop(force, stopDelay, useForceCustomDelay);
                                    }
                                } else if (this._canTease4) {
                                    if (i === 0 || i === 1 || i === 2 || i === 3) {
                                        this._reels[i].spinStop(force, stopDelay, useForceCustomDelay);
                                    }
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    public onReelStopped(reelIndex: number): void {
        this.tease(reelIndex);

        PlayList.play("SND_BaseRotationEnd" + reelIndex);

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        this._reels[reelIndex].visible = false;
        this.emit(Machine.ReelStopped);

        if (this._stoppedReelIndexes === this._reelCount) {
            PlayList.stop("SND_BaseRotation", 10);
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stopTimer.stop();
            if (DEBUG) {
                const old = AppContext.gameConfiguration.MinGameRoundDuration;
                const current = AppContext.gameConfiguration.GameRoundMinSpan;
                const minDuration = current > 0 ? current : old > 0 ? old : 0;
                const spinDuration = Date.now() - this._startSpinTime;

                if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                    this.log("Spin time duration: " + spinDuration + " ms");
                } else {
                    console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
                }
            }

            this.emit(Machine.MachineStopped);
            this._reelsContainer.visible = false;
        }
    }

    protected checkTeasing(reelIndex: number): boolean {
        let count = 0;

        for (let i = 0; i < reelIndex; i++) {
            for (let j = 0; j <= this._stoppedSymbolsArray[i].symbols.length; j++) {
                if (this._stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                    count++;
                }
            }
        }

        return count >= 2;
    }

    protected tease(reelIndex: number): void {
        switch (reelIndex) {
            case 1:
                if (this._canTease2) {
                    ReelExtended.Tease = true;
                    this._reels[2].spinTease([13], 90);

                    PlayList.setVolume("SND_BaseLoop", 0);
                    PlayList.stop("SND_FSLoop");
                    PlayList.play("SND_BaseTeaser");

                    if (this._teasing != null) {
                        (<SwitchElement>this._teasing.childs["Teasing2"]).goToCase("START");
                    }
                }

                break;
            case 2:
                if (this._canTease3) {
                    ReelExtended.Tease = true;
                    this._reels[3].spinTease([13], 90);

                    if (!this._canTease2) {
                        PlayList.setVolume("SND_BaseLoop", 0);
                        PlayList.stop("SND_FSLoop");
                        PlayList.play("SND_BaseTeaser");
                    }

                    if (this._teasing != null) {
                        (<SwitchElement>this._teasing.childs["Teasing3"]).goToCase("START");

                        if (this._canTease2) {
                            if ((<SwitchElement>this._teasing.childs["Teasing2"]).getCurrentCase() == "LOOP") {
                                (<SwitchElement>this._teasing.childs["Teasing2"]).goToCase("STOPPING");
                            }
                        }
                    }
                }

                break;
            case 3:
                if (this._canTease4) {
                    ReelExtended.Tease = true;
                    this._reels[4].spinTease([13], 90);

                    if (!this._canTease3) {
                        PlayList.setVolume("SND_BaseLoop", 0);
                        PlayList.stop("SND_FSLoop");
                        PlayList.play("SND_BaseTeaser");
                    }

                    if (this._teasing != null) {
                        (<SwitchElement>this._teasing.childs["Teasing4"]).goToCase("START");

                        if (this._canTease3) {
                            if ((<SwitchElement>this._teasing.childs["Teasing3"]).getCurrentCase() == "LOOP") {
                                (<SwitchElement>this._teasing.childs["Teasing3"]).goToCase("STOPPING");
                            }
                        }
                    }
                }

                break;
            case 4:
                PlayList.stop("SND_BaseTeaser");

                if (this._canTease4) {
                    if (this._teasing != null) {
                        if ((<SwitchElement>this._teasing.childs["Teasing4"]).getCurrentCase() == "LOOP") {
                            (<SwitchElement>this._teasing.childs["Teasing4"]).goToCase("STOPPING");
                        }
                    }

                    let count = 0;

                    for (let i = 0; i <= reelIndex; i++) {
                        for (let j = 0; j <= this._stoppedSymbolsArray[i].symbols.length; j++) {
                            if (this._stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                                count++;
                            }
                        }
                    }

                    if (count >= 3) {
                        PlayList.play("SND_BaseTeaserWin");
                    } else {
                        PlayList.play("SND_BaseTeaserFail");
                        PlayList.resetVolume("SND_BaseLoop");
                    }
                }

                break;
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/